import { apiSlice } from './apiSlice'

/** Mirrors FastAPI `SaleCreditNoteOut` (camelCase) — frmCtrlSalesQryCredit datasheet. */
export interface SaleCreditNote {
  id: number
  creditNo: string | null
  claimScsCode: string | null
  customerDebitNo: string | null
  creditDate: string | null
  creditAmount: number | null
  doNo: string | null
  loadingPlace: string | null
  dateUpdated: string | null
  personUpdated: string | null
  claimType: string | null
  note: string | null
  currency: string | null
  paymentDate: string | null
  fx: number | null
  priceUnit: string | null
  unit: string | null
  client: string | null
}

export interface SaleCreditNoteListResponse {
  items: SaleCreditNote[]
  total: number
  page: number
  pageSize: number
}

export interface SaleCreditNoteListQuery {
  page: number
  pageSize: number
  deliveryPeriodFrom?: string
  deliveryPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}

export interface SuggestIndexResponse {
  index: number
  exists: boolean
}

export interface CnLookupItem { value: string; label: string }
export interface CnLookupResponse { items: CnLookupItem[]; total: number }

/** GET /{id}/form shape for Edit hydrate. */
export type SaleCreditNoteForm = Record<string, unknown> & { id: number }

/** claim_scs_code_AfterUpdate DLookUps over v_claim_scs (readonly display). */
export interface CreditClaimSnapshot {
  client: string | null
  claimType: string | null
  claimValue: number | null
  claimCurrency: string | null
  invoiceNo: string | null
}

/** Create/Update payload — frmCtrlSalesQryCreditAdd → claim_credit_scs. */
export interface SaleCreditNoteWrite {
  claimScsCode?: string | null
  creditNo?: string | null
  customerDebitNo?: string | null
  creditDate?: string | null
  creditAmount?: number | null
  note?: string | null
  priceUnit?: string | null
  paymentDate?: string | null
  fx?: number | null
}

const saleCreditNoteApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleCreditNotes: builder.query<SaleCreditNoteListResponse, SaleCreditNoteListQuery>({
      query: ({ page, pageSize, deliveryPeriodFrom, deliveryPeriodTo, finalizedFilter }) => ({
        url: '/sale-credit-notes',
        params: {
          page,
          pageSize,
          ...(deliveryPeriodFrom ? { deliveryPeriodFrom } : {}),
          ...(deliveryPeriodTo ? { deliveryPeriodTo } : {}),
          ...(finalizedFilter ? { finalizedFilter } : {}),
        },
      }),
      providesTags: ['SaleCreditNote'],
    }),

    getSaleCreditNote: builder.query<SaleCreditNote, number>({
      query: (id) => `/sale-credit-notes/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleCreditNote', id }],
    }),

    deleteSaleCreditNote: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-credit-notes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleCreditNote'],
    }),

    getSaleCreditNoteCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-credit-notes/access/can-delete',
    }),

    // ── frmCtrlSalesQryCreditAdd ─────────────────────────────────────────────
    getSaleCreditNoteForm: builder.query<SaleCreditNoteForm, number>({
      query: (id) => `/sale-credit-notes/${id}/form`,
    }),
    createSaleCreditNote: builder.mutation<{ claim_credit_scs_id: number }, SaleCreditNoteWrite>({
      query: (body) => ({ url: '/sale-credit-notes', method: 'POST', body }),
      invalidatesTags: ['SaleCreditNote'],
    }),
    updateSaleCreditNote: builder.mutation<{ claim_credit_scs_id: number }, { id: number; body: SaleCreditNoteWrite }>({
      query: ({ id, body }) => ({ url: `/sale-credit-notes/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleCreditNote'],
    }),
    suggestCreditNo: builder.query<SuggestIndexResponse, void>({
      query: () => '/sale-credit-notes/suggest/credit-no',
    }),
    checkCreditNo: builder.query<SuggestIndexResponse, { creditNo: string; excludeId?: number }>({
      query: ({ creditNo, excludeId }) => ({
        url: '/sale-credit-notes/check/credit-no',
        params: { creditNo, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    getCreditClaimSnapshot: builder.query<CreditClaimSnapshot, string>({
      query: (claimScsCode) => ({ url: '/sale-credit-notes/claim-snapshot', params: { claimScsCode } }),
      keepUnusedDataFor: 0,
    }),
    cnLookupClaimCodes: builder.query<CnLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-credit-notes/lookups/claim-codes', params: q ? { q } : {} }),
    }),
    cnLookupPriceUnits: builder.query<CnLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-credit-notes/lookups/price-units', params: q ? { q } : {} }),
    }),
  }),
})

export const {
  useGetSaleCreditNotesQuery,
  useGetSaleCreditNoteQuery,
  useDeleteSaleCreditNoteMutation,
  useGetSaleCreditNoteCanHardDeleteQuery,
  useLazyGetSaleCreditNoteFormQuery,
  useCreateSaleCreditNoteMutation,
  useUpdateSaleCreditNoteMutation,
  useLazySuggestCreditNoQuery,
  useLazyCheckCreditNoQuery,
  useLazyGetCreditClaimSnapshotQuery,
  useCnLookupClaimCodesQuery,
  useCnLookupPriceUnitsQuery,
} = saleCreditNoteApi

export default saleCreditNoteApi
